import React from "react";
import { BarChart3, TrendingUp, Users, Calendar, ArrowUpRight, MessageSquare, CheckCircle2 } from "lucide-react";
import SagentLogo from "./SagentLogo";

const DashboardPreview = () => {
  return (
    <section className="py-24 overflow-hidden relative bg-white">
      <div className="absolute inset-0 bg-grid opacity-30" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <p className="text-cyan-600 font-mono text-sm tracking-[0.3em] uppercase font-bold">Your Operating System</p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-['Lexend'] tracking-tight">
            The Command Center for <span className="text-cyan-600">Your Sales</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Sagent isn't just a chatbot. It's a complete operating system for your villa business.
          </p>
        </div>

        {/* Dashboard Mockup — Fully Coded */}
        <div className="relative group max-w-5xl mx-auto">
          {/* Glow on hover */}
          <div className="absolute -inset-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition duration-700" />
          
          {/* Dashboard Container */}
          <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl shadow-slate-200/80 border border-black/5">
            
            {/* Dashboard Top Bar */}
            <div className="bg-slate-900 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <SagentLogo className="w-4 h-4 text-cyan-400" />
                  <span className="text-white/70 text-xs font-mono">Sagent Command Center</span>
                </div>
              </div>
              <div className="text-white/40 text-[10px] font-mono">dashboard.sagent.ai</div>
            </div>

            {/* Dashboard Body */}
            <div className="p-4 sm:p-6 bg-slate-50/50">
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                {[
                  { label: "Enquiries", value: "47", change: "+12%", icon: MessageSquare, color: "text-blue-600", bg: "bg-blue-50" },
                  { label: "Bookings", value: "12", change: "+23%", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
                  { label: "Conv. Rate", value: "25.5%", change: "+4.2%", icon: TrendingUp, color: "text-cyan-600", bg: "bg-cyan-50" },
                  { label: "Revenue", value: "₹2.1L", change: "+18%", icon: BarChart3, color: "text-violet-600", bg: "bg-violet-50" },
                ].map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl p-3 sm:p-4 border border-black/5 shadow-sm">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                        <stat.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${stat.color}`} />
                      </div>
                      <span className="text-[9px] sm:text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                        <ArrowUpRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                        {stat.change}
                      </span>
                    </div>
                    <p className="text-lg sm:text-xl font-extrabold text-slate-900 font-['Lexend'] tracking-tight">{stat.value}</p>
                    <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Chart + Activity Row */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Mini Chart Area */}
                <div className="lg:col-span-3 bg-white rounded-xl p-4 sm:p-5 border border-black/5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 font-['Lexend']">Enquiry Trend</h4>
                    <div className="flex gap-2">
                      <span className="text-[9px] sm:text-[10px] font-bold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">This Week</span>
                      <span className="text-[9px] sm:text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Last Week</span>
                    </div>
                  </div>
                  {/* Simulated Bar Chart */}
                  <div className="flex items-end gap-1.5 sm:gap-2 h-24 sm:h-28">
                    {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-cyan-500/80 rounded-t-sm sm:rounded-t-md transition-all duration-500 hover:bg-cyan-600" 
                          style={{ height: `${h}%` }}
                        />
                        <span className="text-[8px] sm:text-[9px] text-slate-400 font-mono">
                          {["M", "T", "W", "T", "F", "S", "S"][i]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="lg:col-span-2 bg-white rounded-xl p-4 sm:p-5 border border-black/5 shadow-sm">
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 font-['Lexend'] mb-4">Live Activity</h4>
                  <div className="space-y-3">
                    {[
                      { text: "Booking confirmed — ₹34,000", time: "2m ago", dot: "bg-emerald-500" },
                      { text: "New enquiry — 6 guests, Dec 15", time: "5m ago", dot: "bg-blue-500" },
                      { text: "Price negotiation — ₹22K → ₹20K", time: "8m ago", dot: "bg-amber-500" },
                      { text: "Follow-up sent to Rahul M.", time: "12m ago", dot: "bg-violet-500" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${item.dot} mt-1.5 flex-shrink-0`} />
                        <div className="min-w-0">
                          <p className="text-[10px] sm:text-xs text-slate-700 font-medium truncate">{item.text}</p>
                          <p className="text-[8px] sm:text-[10px] text-slate-400 font-mono">{item.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;
